import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// MUI
import { CircularProgress, Stack, Box, Grid, Typography } from '@mui/material';

// Custom components
import NoRecordsFound from 'components/ListView/EmptyRecords/noRecordsFound.cmp';
import InfiniteVirtualList from 'components/InfiniteVirtualList/InfiniteVirtualList';

// Utils
import useResponsive from 'utils/CustomHooks/useResponsive';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';

// APIs
import xeniaApi from 'api/index';

// Other Libraries
import { debounce } from 'lodash';
import {
  Card,
  CardsContainer,
  Header,
  PreBuildReportsCnt,
  ReportCard,
  ReportColumn,
} from './styles';
import GridBuilderIcon from 'components/Icons/templateStepIcons2/gridBuilderIcon';
import CustomDashboardIcon from 'components/Icons/sidebarIcons/customdashboardIcon';
import ChecklistIcon from 'components/Icons/sidebarIcons/checklistIcon';
import ApprovalReportIcon2 from 'components/Icons/sidebarIcons/approvalsReportIcon2';
import PulseDashboardIcon from 'components/Icons/sidebarIcons/pulseDashboardIcon';
import GalleryViewIcon from 'components/Icons/sidebarIcons/galleryViewIcon';
import TemplateExportIcon from 'components/Icons/sidebarIcons/templateExportIcon';
import UsersAndTeamsIcon from 'components/Icons/sidebarIcons/usersAndTeamsIcon';
import { HasPermission } from 'components/HasPermission';
import { useCustomPermission } from 'utils/CustomHooks/useCustomPermission';
import { getReportsState } from 'store/selectors/reports';
import { dashboardImages } from './reportImages';
import TaskListWidgetEmptyState from 'pages/Widgets/TaskListing/TaskListView/EmptyRecords/TaskListWidgetEmptyState.cmp';

interface GridReportProps {
  listHeight?: number;
  searchQuery?: string;
}

const taskCompletionSlugs = [
  'daily-compliance-report',
  'scheduled-work-summary-table',
  'schedule-report',
  'task-summary',
  'employee-productivity',
];

const templateSubmissionSlugs = [
  'template-submissions',
  'approvals-report',
  'template-exports',
];
const locationSpecificSlugs = ['location-pulse-dashboard', 'gallery-view'];

const fallbackTitles = {
  'daily-compliance-report': 'Daily Compliance Report',
  'scheduled-work-summary-table': 'Scheduled Work Summary Report',
  'schedule-report': 'Schedule Completion Report',
  'task-summary': 'Task Summary',
  'employee-productivity': 'Employee Productivity Report',
  'template-submissions': 'Template Submissions Report',
  'approvals-report': 'Approvals Report',
  'template-exports': 'Template Exports',
  'location-pulse-dashboard': 'Location Pulse Dashboard',
  'gallery-view': 'Gallery View',
};

function buildPrebuiltReportColumns(predefinedReports) {
  const buildColumn = (slugs) =>
    slugs.map((slug) => {
      const found = predefinedReports.find((r) => r.slug === slug);
      return {
        slug,
        title: found?.name || fallbackTitles[slug] || slug,
        icon: dashboardImages[slug],
        id: found?.id,
      };
    });
  return {
    taskCompletion: buildColumn(taskCompletionSlugs),
    templateSubmission: buildColumn(templateSubmissionSlugs),
    locationSpecific: buildColumn(locationSpecificSlugs),
  };
}

const PreBuildReports: React.FC<GridReportProps> = ({
  listHeight = 500,
  searchQuery = '',
}) => {
  const { PERMISSIONS } = useCustomPermission();
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();
  const predefinedReports = useSelector(getReportsState) || [];

  const columns = buildPrebuiltReportColumns(predefinedReports);

  // Filter reports based on search query
  const filterReports = (reports: any[]) => {
    if (!searchQuery) return reports;
    return reports.filter((report) =>
      report.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const filteredColumns = {
    taskCompletion: filterReports(columns.taskCompletion),
    templateSubmission: filterReports(columns.templateSubmission),
    locationSpecific: filterReports(columns.locationSpecific),
  };

  const handleReportClick = useCallback(
    (report) => {
      // Handle location-specific reports
      if (report.slug === 'location-pulse-dashboard') {
        navigateWithWorkspaceUrl('/dashboard?type=PULSE');
        return;
      }

      if (report.slug === 'gallery-view') {
        navigateWithWorkspaceUrl('/gallery');
        return;
      }

      // Handle template submission reports
      if (report.slug === 'template-exports') {
        navigateWithWorkspaceUrl('/dashboards?tab=template-exports');
        return;
      }

      // Handle template reports that have an ID
      if (
        report.id &&
        (report.slug === 'template-submissions' ||
          report.slug === 'approvals-report')
      ) {
        navigateWithWorkspaceUrl(`/dashboards/template/${report.id}`);
        return;
      }

      // Handle task completion reports - navigate to grid report with specific report
      if (taskCompletionSlugs.includes(report.slug)) {
        navigateWithWorkspaceUrl(`/grid-report/${report.slug}`);
        return;
      }

      // Default fallback - navigate to grid report
      navigateWithWorkspaceUrl(`/grid-report/${report.slug}`);
    },
    [navigateWithWorkspaceUrl],
  );

  return (
    <PreBuildReportsCnt>
      {/* Task Completion Analytics */}
      {filteredColumns.taskCompletion.length > 0 && (
        <ReportColumn>
          <Typography className="title">Task Completion Analytics</Typography>
          <HasPermission permissions={[PERMISSIONS.CAN_VIEW_REPORTING]}>
            {filteredColumns.taskCompletion.map((report) => (
              <ReportCard
                key={report.slug}
                onClick={() => handleReportClick(report)}
              >
                {report.icon}
                <Typography className="reportTitle">{report.title}</Typography>
              </ReportCard>
            ))}
          </HasPermission>
        </ReportColumn>
      )}

      {/* Template Submission Reports */}
      {filteredColumns.templateSubmission.length > 0 && (
        <ReportColumn>
          <Typography className="title">Template Submission Reports</Typography>
          <HasPermission permissions={[PERMISSIONS.CAN_VIEW_REPORTING]}>
            {filteredColumns.templateSubmission.map((report) => (
              <ReportCard
                key={report.slug}
                onClick={() => handleReportClick(report)}
              >
                {report.icon || <ChecklistIcon />}
                <Typography className="reportTitle">{report.title}</Typography>
              </ReportCard>
            ))}
          </HasPermission>
        </ReportColumn>
      )}

      {/* Location Specific Views */}
      {filteredColumns.locationSpecific.length > 0 && (
        <ReportColumn>
          <Typography className="title">Location Specific Views</Typography>
          <HasPermission permissions={[PERMISSIONS.CAN_VIEW_REPORTING]}>
            {filteredColumns.locationSpecific.map((report) => (
              <ReportCard
                key={report.slug}
                onClick={() => handleReportClick(report)}
              >
                {report.icon || <ChecklistIcon />}
                <Typography className="reportTitle">{report.title}</Typography>
              </ReportCard>
            ))}
          </HasPermission>
        </ReportColumn>
      )}

      {/* Show message when no results found */}
      {searchQuery &&
        filteredColumns.taskCompletion.length === 0 &&
        filteredColumns.templateSubmission.length === 0 &&
        filteredColumns.locationSpecific.length === 0 && (
          <Box display={'flex'} justifyContent="center" width={'100%'} mt={2}>
            <TaskListWidgetEmptyState />
          </Box>
        )}
    </PreBuildReportsCnt>
  );
};

export default PreBuildReports;
